/**
 * Tukey's honestly significant difference (HSD) test, in the Tukey–Kramer form for
 * unequal group sizes, following a one-way ANOVA.
 *
 * With k groups, pooled variance MS_W on df_W = N − k degrees of freedom, and group sizes
 * nᵢ and nⱼ, the standard error of the difference of two means is
 *
 *   SE = √( MS_W / 2 · (1/nᵢ + 1/nⱼ) )
 *
 * and q = |x̄ᵢ − x̄ⱼ| / SE is referred to the studentized range distribution with k means
 * and df_W degrees of freedom. The adjusted p-value is P(Q ≥ q); the simultaneous
 * (1 − α) interval for the difference is x̄ᵢ − x̄ⱼ ± q_crit · SE with q_crit the (1 − α)
 * quantile. This is R's `TukeyHSD(aov(y ~ g))` and `scipy.stats.tukey_hsd`.
 *
 * With equal sizes n the single "honestly significant difference" is q_crit · √(MS_W / n):
 * any two means further apart than that differ significantly.
 *
 * The compact letter display puts a letter on each group so that groups sharing a letter
 * are not significantly different (groups run from the highest mean, letter "a", down).
 */

import {
  type OneWayAnova,
  oneWayAnova,
} from "@/lib/tool-kit/stats-tests/anova";
import {
  ptukeyUpper,
  qtukey,
} from "@/lib/tool-kit/stats-tests-more/studentized-range";

export interface TukeyPair {
  first: number;
  second: number;
  /** mean(first) − mean(second). */
  difference: number;
  se: number;
  q: number;
  p: number;
  lower: number;
  upper: number;
  /** The difference in pooled-SD units: (x̄ᵢ − x̄ⱼ) / √MS_W. */
  d: number;
}

export interface TukeyResult {
  anova: OneWayAnova;
  k: number;
  alpha: number;
  qCritical: number;
  /** q_crit · √(MS_W / n) when every group has the same size, else null. */
  hsd: number | null;
  pairs: TukeyPair[];
  /** Letters for each group, in the order the groups were given. */
  letters: string[];
}

export type TukeyFailure =
  | "too-few-groups"
  | "too-few-values"
  | "no-variance"
  | "too-few-df";

export type TukeyOutcome =
  | { ok: true; result: TukeyResult }
  | { ok: false; reason: TukeyFailure };

/**
 * Compact letters from a pairwise-significance matrix.
 *
 * Groups are ordered from the highest mean; each maximal run of neighbours in that order
 * whose members are all pairwise non-significant gets the next letter, and a group takes the
 * letter of every run it belongs to.
 */
export function compactLetters(
  means: readonly number[],
  significant: (a: number, b: number) => boolean
): string[] {
  const order = means
    .map((_, index) => index)
    .sort((a, b) => means[b] - means[a] || a - b);
  const runs: [number, number][] = [];
  for (let start = 0; start < order.length; start++) {
    let end = start;
    while (end + 1 < order.length) {
      const candidate = order[end + 1];
      const clash = order
        .slice(start, end + 1)
        .some((member) => significant(member, candidate));
      if (clash) {
        break;
      }
      end += 1;
    }
    const contained = runs.some(([s, e]) => s <= start && e >= end);
    if (!contained) {
      runs.push([start, end]);
    }
  }
  const letters = means.map(() => "");
  for (const [runIndex, [start, end]] of runs.entries()) {
    const letter = String.fromCharCode(97 + (runIndex % 26));
    for (const member of order.slice(start, end + 1)) {
      letters[member] += letter;
    }
  }
  return letters;
}

export function tukeyHsd(
  groups: readonly (readonly number[])[],
  alpha = 0.05
): TukeyOutcome {
  const outcome = oneWayAnova(groups);
  if (!outcome.ok) {
    return { ok: false, reason: outcome.reason };
  }
  const anova = outcome.result;
  const k = groups.length;
  const dfWithin = anova.df2;
  if (dfWithin < 2) {
    return { ok: false, reason: "too-few-df" };
  }
  const qCritical = qtukey(1 - alpha, k, dfWithin);
  const sizes = anova.groups.map((group) => group.n);
  const pairs: TukeyPair[] = [];
  for (let i = 0; i < k; i++) {
    for (let j = i + 1; j < k; j++) {
      const difference = anova.groups[i].mean - anova.groups[j].mean;
      const se = Math.sqrt(
        (anova.msWithin / 2) * (1 / sizes[i] + 1 / sizes[j])
      );
      const q = Math.abs(difference) / se;
      pairs.push({
        first: i,
        second: j,
        difference,
        se,
        q,
        p: Math.min(1, ptukeyUpper(q, k, dfWithin)),
        lower: difference - qCritical * se,
        upper: difference + qCritical * se,
        d: difference / Math.sqrt(anova.msWithin),
      });
    }
  }
  const significant = (a: number, b: number) => {
    const pair = pairs.find(
      (candidate) =>
        (candidate.first === a && candidate.second === b) ||
        (candidate.first === b && candidate.second === a)
    );
    return pair ? pair.p < alpha : false;
  };
  const equal = sizes.every((size) => size === sizes[0]);
  return {
    ok: true,
    result: {
      anova,
      k,
      alpha,
      qCritical,
      hsd: equal ? qCritical * Math.sqrt(anova.msWithin / sizes[0]) : null,
      pairs,
      letters: compactLetters(
        anova.groups.map((group) => group.mean),
        significant
      ),
    },
  };
}
